/**
 * officeSend.js — WHICH OFFICE HAS THIS PATIENT'S CASE REPORT? s99 (D475)
 *
 * Dr. Rami 2026-10-10 ("Orella Shared Office" mockup): once a report is sent, that office takes
 * the place of "Send to your doctor's office" on the share hub, and Case Home shows it as "Sent
 * to" until the doctor accepts the case (which sets "Your doctor"). A failed send does not
 * persist: the patient sees one notice, then the hub offers the send again.
 *
 * ★ THE SERVER DECIDES "CURRENT". GET /cases/<id>/fax-sends marks each send `current` (the share
 *   code it carried is still the case's code). "Send to a different office" withdraws the case,
 *   which retires every earlier send at once; nothing here re-derives that.
 * ★ Sends arrive NEWEST FIRST. PURE functions: the hub and Case Home import them, never copy them.
 */

const FAILED = 'failed'

/** The newest current send that did not fail (queued, sending or delivered), or null. */
export function currentOfficeSend(sends) {
  if (!Array.isArray(sends)) return null
  return sends.find((s) => s && s.current !== false && s.status !== FAILED) || null
}

/**
 * The failed send to tell the patient about ONCE: the newest current send, when it failed and
 * nothing newer went out. `seen` is the set of send ids already shown.
 */
export function unseenFailedSend(sends, seen) {
  if (!Array.isArray(sends)) return null
  const newest = sends.find((s) => s && s.current !== false)
  if (!newest || newest.status !== FAILED) return null
  if (seen && typeof seen.has === 'function' && seen.has(newest.id)) return null
  return newest
}

/** The office as lines to draw. The directory's details win; the send's own names fill gaps. */
export function officeLines(send) {
  if (!send) return null
  const o = send.office || {}
  const name = o.name ? [o.name, o.credential].filter(Boolean).join(', ') : (send.recipientName || '')
  const a = o.address || {}
  const street = [a.line1, a.line2].filter(Boolean).join(', ')
  const cityLine = [[a.city, a.state].filter(Boolean).join(', '), a.zip].filter(Boolean).join(' ')
  return {
    name: name || send.recipientPractice || '',
    specialty: o.specialty || '',
    practice: send.recipientPractice || '',
    street,
    cityLine,
    phone: o.phone || '',
    fax: send.faxNumber || '',
  }
}

// One-time failure notice: which failed sends the patient has already been shown.
const SEEN_KEY = 'orella_fax_fail_seen'
export function readSeenFailures() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')) } catch { return new Set() }
}
export function markFailureSeen(id) {
  if (!id) return
  try {
    const s = readSeenFailures(); s.add(id)
    localStorage.setItem(SEEN_KEY, JSON.stringify([...s].slice(-50)))
  } catch { /* storage refused: the notice may show again, never blocks */ }
}
